export type VisitModality = 'in_person' | 'video' | 'audio' | null;

export interface ResolvedVisitMode {
  modality: VisitModality;
  jotMeet: boolean;
}

/**
 * Single source of truth for turning the stored visit-mode fields (modality +
 * telehealth) into what should actually be shown/used. Two rules that must stay
 * consistent across every surface:
 *
 *  - JotMeet is only active when the visit is telehealth AND the org holds the
 *    telehealth entitlement. A stored telehealth flag without the entitlement is
 *    dormant intent, not a live "+ JotMeet" default.
 *  - A telehealth visit with no explicit modality is implicitly a video visit
 *    (the telehealth boolean tells us it's virtual), so we infer 'video'.
 */
export const resolveVisitMode = (
  modality: VisitModality,
  telehealth: boolean,
  telehealthEnabled: boolean,
): ResolvedVisitMode => ({
  modality: modality ?? (telehealth ? 'video' : null),
  jotMeet: telehealth && telehealthEnabled,
});

/** Display label for a visit mode (modality + optional "+ JotMeet"). */
export const formatVisitModeLabel = (
  modality: VisitModality,
  telehealth: boolean,
  telehealthEnabled: boolean,
): string => {
  const resolved = resolveVisitMode(modality, telehealth, telehealthEnabled);
  if (resolved.modality === 'video') return resolved.jotMeet ? 'Video + JotMeet' : 'Video';
  if (resolved.modality === 'audio') return 'Phone';
  if (resolved.modality === 'in_person') return 'In-person';
  return 'Visit mode not set';
};
